import { createMachine, assign } from 'xstate';

import * as Reporting from '../../../analytics/Reporting';
import { closeStepMock, execStepAction, saveProgressToEndUserStore } from './actions';
import {
  passesAudienceConditions,
  passesFrequencyCondition,
  passesPageConditions,
  hasRemainingSteps,
  passesMaxRenderedNudges,
  passesStatus,
  passesTriggerMatch,
  getNudgeDataFromUserStore,
  passesFlip,
} from './selectors';
import { renderNudge } from '../../../components/nudges/RenderNudge';
import { openEditorIfLoaded } from '../../util/editorUtils';
import StepConditionsMachine from './nudgeStepMachine';

import type { INudgeType } from '@commandbar/internal/middleware/types';
import type { EngineState } from '../state';
import type { TriggerEvent } from './nudgeManagerMachine';

export type NudgeContext = {
  nudge: INudgeType;
  nudgeSeenThisSessionTs: Array<number>;
  triggerEvent: TriggerEvent | null;
  stepIndex: number;
  prevPassedConditions: boolean;
  nudgeDismissed: boolean;
  nudgeCompleted: boolean;
};

export type NudgeEvents =
  | TriggerEvent
  | { type: 'DISMISS' }
  | { type: 'ADVANCE'; isPinClick?: boolean; button_type?: 'primary' | 'secondary' | 'help_doc' }
  | { type: 'RESET_STATE'; step: number };

type NudgeServices = { stepConditions: { data: { stepChecksFailed: boolean } } };

const NudgeMachine = (_: EngineState, nudge: INudgeType) => {
  const savedStep = getNudgeDataFromUserStore(_, nudge.id)?.currentStep ?? 0;

  const nudgeContext: NudgeContext = {
    nudge,
    stepIndex: savedStep > nudge.steps.length - 1 ? nudge.steps.length - 1 : savedStep,
    nudgeSeenThisSessionTs: [],
    triggerEvent: null,
    prevPassedConditions: false,
    nudgeDismissed: false,
    nudgeCompleted: false,
  };

  return createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QDkCu0wGIBKBRAyrgCoD6+RAgkbgNoAMAuoqAA4D2sAlgC6dsB2zEAA9EAWgCsAFgBMAOgDssiQGYpqqQA4AnHSkAaEAE9xMzXJkBGAGwyJ1lSoV6pTqQF93htBjkBJCAAbLCJsPwBxcNxseiYkEHYuXgEhUQQxFU1zJ20JS20FTWsJHRVDEwRnC1ypOh183Ilta09vdBg5AGEACzAAYwBrTn4oAAJOgQgePn5YLt7B4bGKdE4wfj6sWKFE6ZT4tLFa+RtNJsspa007JXLESzprazltGQUZFWslGQKJCVaQD4Oj1+kMRuNJntZvNQUtRisputNpgaJY4qwOFDUog6HJVJoFMVpA4VA8VBIZHcEA9XC8JI8SlJLJYFE5NACgWAYYtwRN+FNktCQTyxgAFACGMBRjB2mMF2PSmWydFUdBkrhU2iZVMsKjqeLOeRUZly2iyHPaXOFYLGfIFMzm1rhEqlqPRCTlMwVuPxhL+l0cZIpOrMEjk1jqZtJLgUCgtvidvMhgsdCxtowAYgAnMAAR1QSKM0vdu3lB3EtSkeNcEgUumcEYJVM0pLkWiur3plisZnjwLTcLtUNTsPB2bzBY2RbdsqSXvL1OeLcamWaP0JTeMiBbljbRR0djo3e77K8gMt3PTQ5Tl7h+G44u4qFgxdnWIXGVeeK1nz1DleUgKFSEZVnoVhqASWQRtofZWgOSb8sOt7gvej7PiiaJvmWoBpDYcjLn8q62AUXxlFuCAgXIYGWCUuT5JYOiwchtrJg6zGjEQWacFAMBZqMACyj59N0r7xKW844RW3Z4oUrJnDI1j5OSVK1GGLKaHQhKPMe2hakxiYsYhN4GRxXE8WAfGCdwwkYSWnr7JJi74ZqhE6MRhKsipKpyOpmkRopMgyPp8GGfaAgjiKmaBJwLCiRic4OSIFZqvhLbHE8JL1jqjYWHkRTFFI9YOMFo6hUhJkZtFsUzmJ9mCAueEEZcdYkkGOr2FWTwKHkHxmg8jxMfeYCxRAAhcrAD7cFynJyENLDbLVCX1Y5tQvHYhV0Lk1y1AxVJiNJlY5Ma7yPH6g1TSw7HXg6mCjfwXLDAAbmwAzTRec1Xax4UIE9bB9I+MyxAt8Xvo5zJMnSNiFfkthXJYVIUrigF6ASLJrrI53DZ9Rk3XdD38M9r1yDNH0mdd32-f9gpA5hi2g0lCBvLigXvN12gMdcahAeRiNts4WgKGjtgY2eJMXexgnCKM2DrBAFmQHFHpLQqPwqHIahSOoilZC2hJUt15j5IFJ5c25mOXSZkvS7L8sQLZWESQzhRq7qlbdR8zIfPrJQ+a8PZpYUZrm3IMv8hZowADJsGwsUACJ+Pg-EJ-gwNK-TuEfJ1lxatYrhKHWIb0m2gFvNYZoUrYKjB6Hct8VHMchzbXEjJgFCxwAahQyCdLQMp09hDOfEjFx1i2ZyKaoOp6riThl7nWgVx81dN5H0eXRQECPeKGxLIr4mJWk3W7u8LOI38hQ6prYaEnq5IXLI6iWMvYd12vcgb1vO8tzVIMDxnxqKGNFkTUbgC7kQuAAswud2Z-jMEUZ+tdV4NzJl9WYt0xpyF+kTMWWMa7h3rhbEKEIcYUwJn9AGAggZ91-o7XCFxdx0UyqA7QOobDyB0KyTSPwzQtirqLd64s8Gv2QUQ8maC8aYLIdggRuCV4EOxmFWYP0yFU0BowH+ac-6IDLriTQx4GKZFOgYcBbD8IFD1O8XSwC+Fnn4GwOW8B4icgdgfcQucwzszOEoFkPU9F7WZNfNhfwjxFD1D8JiARgguOWgzDI6hwxXFrI8Ogd9x57TDL1Mu6h7ApMyCVSKYjHE0NcekJoCgXgMVrEyI+Zh4bgIKC8PRThmkpK1JpfJV5UERXTAiNYGwwDRIVEcVsNFElHgKFYGw3MKgATxE0J464oJ5P4QmURXT2IugGf3WhUlzD0k0K4QKVhFJZNYV8NsWTtBOEeOoXsKz+ylWIYo7pcJxz5kLIMj8FJtAvDVFqPIrwHC6mAi2RpZwlknzLh0wc6yTKoSfEUzROzFS6EULGGo0glCbUpORLQx9ml2Fnk8Gi0KELPPYpxbivEBJCW6J8xyRxbD4UFu8VQrIfg0QkFSUMe5dSsk4ZqWocZ7lwUeYU9ilUYr0tidIH5niqk+JPHUiouo9G+z5d8uszVzbSrSFYKspJDHBLvuqaZ9wlD4WkHomiNEZB0E2i0EVs1xaxzGrq+4xyqKKXtaUfOLDyKsl3Ebf2ptmjBxQSQ2Y7rGZMsNRpY1PVAJUj1D88kWhNLkmuFqQC4aiFWyEZAaNCl5BxpVPSE1XLyLQTxA4VUhV1wKQQfgteRbc4+SsBpA53rLFkQqAc7I5jnCdnnkFJ1H0hFIMukIpY0agEWGcApIVdrNqVpVd2KsHDSTdk1ApfITbhHr03tvPoM7tklNJCcDtdRLgPB7ZfPCZpmg2FgVBfdk6FHDmjRGJGy4rmNFrJoQuu5Z7NE1vJSunhPBAA */
      id: 'Nudge',

      initial: 'Idle',

      states: {
        Idle: {
          on: {
            TRIGGER: {
              target: 'Checking Conditions',
              actions: ['setTriggerEvent'],
            },
          },
        },

        'Checking Conditions': {
          initial: 'Checking Trigger Match',

          states: {
            'Checking Audience': {
              always: [
                {
                  target: 'Checking Page',
                  cond: 'passesAudience',
                },
                {
                  target: '#Nudge.Idle',
                  actions: ['setFailedConditions'],
                },
              ],

              description: `Are audience conditions satisfied?`,
            },

            'Checking Page': {
              always: [
                {
                  target: 'Checking Flip',
                  cond: 'passesPage',
                },
                {
                  target: '#Nudge.Idle',
                  actions: ['setFailedConditions'],
                },
              ],

              description: `Are page conditions satisfied?`,
            },

            'Checking Frequency': {
              always: [
                {
                  target: 'Checking Audience',
                  cond: 'passesFrequency',
                },

                {
                  target: '#Nudge.Idle',
                },
              ],

              description: `Has this specific nudge been seen the maximum number of times?`,
            },

            'Checking Status': {
              always: [
                {
                  target: 'Checking Frequency',
                  cond: 'passesStatus',
                },

                {
                  target: '#Nudge.Idle',
                },
              ],

              description: `Is the nudge live?`,
            },

            'Checking Trigger Match': {
              always: [
                {
                  target: 'Checking Status',
                  cond: 'passesTriggerMatch',
                },
                { target: '#Nudge.Idle' },
              ],

              description: `Is the trigger event applicable to this nudge?`,
            },

            'Checking Flip': {
              always: [
                {
                  target: '#Nudge.Step',
                  cond: 'passesFlip',
                  actions: 'setPassedConditions',
                },
                '#Nudge.Idle',
              ],

              description: `No limit nudges will respond to all "when_conditions_pass" triggers. These are emitted whenever anything in context changes so we could have a lot of triggers emitted continuously.
This check ensures we only respond if conditions relevant to this nudge change.`,
            },
          },
        },

        Step: {
          initial: 'Checking Conditions',

          states: {
            Done: {
              type: 'final',
              entry: 'reOpenEditor',
            },

            'Checking Conditions': {
              invoke: {
                src: 'stepConditions',

                onDone: [
                  {
                    target: 'Done',
                    cond: 'stepChecksFailed',
                  },
                  {
                    target: 'Checking Max Rendered',
                  },
                ],
              },

              description: `This is where we check any step specific conditions. 
This includes searching for elements to pin to or checking availability of commands on a CTA.`,
            },

            'Checking Max Rendered': {
              always: [
                {
                  target: 'Render Loop',
                  cond: 'passesMaxNudgesRendered',
                },
                'Done',
              ],

              description: `Are we already rendering the maximum number of simultaneous nudges?
Limit is currently hard-coded to 1`,
            },

            'Render Loop': {
              entry: ['markSeen', 'reportSeen', 'saveSeen'],
              states: {
                Rendering: {
                  entry: ['renderStep'],
                  exit: 'closeStep',

                  on: {
                    ADVANCE: 'Advancing',
                  },
                },

                Advancing: {
                  entry: ['execAction', 'reportInteracted', 'saveInteraction'],

                  always: [
                    {
                      target: 'Checking Conditions',
                      cond: 'remainingSteps',
                      actions: 'incrementStep',
                    },
                    {
                      target: '#Nudge.Step.Done',
                      actions: ['reportCompleted', 'markCompleted', 'saveInteraction', 'resetStep'],
                    },
                  ],
                },

                'Checking Conditions': {
                  invoke: {
                    src: 'stepConditions',

                    onDone: [
                      {
                        target: '#Nudge.Step.Done',
                        cond: 'stepChecksFailed',
                      },
                      {
                        target: 'Rendering',
                      },
                    ],
                  },

                  description: `Perform the same step conditions check that occurs before the render loop.`,
                },
              },

              initial: 'Rendering',

              description: `Once a nudge has rendered, it should not be interrupted by other nudges until it is dismissed or cannot render its next step.
The nudge manager will keep track of how many nudges are in a render loop. If we are at the limit, we will not forward any triggers.`,

              on: {
                DISMISS: {
                  target: 'Done',
                  actions: ['reportInteracted', 'reportDismissed', 'markDismissed', 'saveInteraction'],
                },
              },
            },
          },

          onDone: 'Idle',
          entry: ['setStepIndex'],
        },
      },

      context: nudgeContext,
      schema: { events: {} as NudgeEvents, context: {} as NudgeContext, services: {} as NudgeServices },
      predictableActionArguments: true,
      preserveActionOrder: true,
      tsTypes: {} as import('./nudgeMachine.typegen').Typegen0,

      on: {
        RESET_STATE: {
          target: '.Idle',
          actions: 'setStepIndex',
        },
      },
    },

    {
      guards: {
        passesMaxNudgesRendered: ({ triggerEvent }) =>
          triggerEvent?.overrides?.maxRenderedNudges || passesMaxRenderedNudges(_),
        passesTriggerMatch: ({ triggerEvent, nudge }) => passesTriggerMatch(_, nudge, triggerEvent),
        passesStatus: ({ triggerEvent, nudge }) => triggerEvent?.overrides?.status || passesStatus(nudge),
        passesFrequency: ({ triggerEvent, nudge }) =>
          triggerEvent?.overrides?.frequency || passesFrequencyCondition(_, nudge),
        passesAudience: ({ triggerEvent, nudge }) =>
          triggerEvent?.overrides?.audience || passesAudienceConditions(_, nudge),
        passesPage: ({ triggerEvent, nudge }) => triggerEvent?.overrides?.page || passesPageConditions(_, nudge),
        passesFlip: ({ triggerEvent, prevPassedConditions }) => passesFlip(_, prevPassedConditions, triggerEvent),

        // step specific
        remainingSteps: hasRemainingSteps(nudge),
        stepChecksFailed: (_context, event) => !!event.data?.stepChecksFailed,
      },
      actions: {
        setTriggerEvent: assign({
          triggerEvent: (_context, triggerEvent) => triggerEvent,
        }),

        setPassedConditions: assign({
          prevPassedConditions: (_context) => true,
        }),

        setFailedConditions: assign({
          prevPassedConditions: (_context) => false,
        }),

        // step specific
        renderStep: ({ stepIndex }) => {
          renderNudge(_, nudge, { stepIndex });
        },
        incrementStep: assign({ stepIndex: ({ stepIndex }) => stepIndex + 1 }),
        closeStep: ({ stepIndex }) => {
          closeStepMock(_, nudge, stepIndex);
        },
        execAction: ({ stepIndex, triggerEvent }, event) => {
          triggerEvent?.overrides?.ctaAction || execStepAction(_, nudge, stepIndex, event);
        },
        reportSeen: ({ stepIndex, triggerEvent }) => {
          if (triggerEvent?.overrides?.reporting) return;

          Reporting.nudgeShown(nudge, stepIndex);
        },
        reportInteracted: ({ stepIndex, triggerEvent }) => {
          if (triggerEvent?.overrides?.reporting) return;

          Reporting.nudgeStepClicked(nudge, stepIndex);
        },
        reportCompleted: ({ stepIndex, triggerEvent }) => {
          if (triggerEvent?.overrides?.reporting) return;

          Reporting.nudgeCompleted(nudge, stepIndex);
        },
        reportDismissed: ({ stepIndex, triggerEvent }) => {
          if (triggerEvent?.overrides?.reporting) return;

          Reporting.nudgeDismissed(nudge, stepIndex);
        },
        saveSeen: ({ stepIndex, triggerEvent }) => {
          if (triggerEvent?.overrides?.saveProgress) return;

          saveProgressToEndUserStore(_, { nudge, stepIndex, addSeenTime: true });
        },
        saveInteraction: ({ stepIndex, triggerEvent, nudgeCompleted, nudgeDismissed }) => {
          if (triggerEvent?.overrides?.saveProgress) return;

          saveProgressToEndUserStore(_, {
            nudge,
            stepIndex,
            nudgeInteracted: true,
            nudgeCompleted,
            nudgeDismissed,
          });
        },
        markSeen: assign({
          nudgeSeenThisSessionTs: ({ nudgeSeenThisSessionTs }, _event) => [...nudgeSeenThisSessionTs, Date.now()],
        }),
        markDismissed: assign({
          nudgeDismissed: (_context, _event) => true,
        }),
        markCompleted: assign({
          nudgeCompleted: (_context, _event) => true,
        }),
        setStepIndex: assign({
          stepIndex: ({ triggerEvent, stepIndex }, event) => {
            if (event.type === 'RESET_STATE') {
              return event.step;
            }

            return triggerEvent?.overrides?.stepIndex ?? stepIndex;
          },
        }),

        resetStep: assign({
          stepIndex: (_context, _event) => 0,
        }),

        reOpenEditor: ({ triggerEvent }) => {
          /** This is part of the "Preview" experience. The editor closes when a nudge is previewed and re-opens when the preview is completed. */
          if (triggerEvent?.overrides?.preview) openEditorIfLoaded();
        },
      },
      services: {
        stepConditions: ({ stepIndex, triggerEvent }) => StepConditionsMachine(_, { nudge, stepIndex, triggerEvent }),
      },
    },
  );
};

export default NudgeMachine;
